import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { useNavigate, useLocation } from "react-router-dom";
import FilterSection from "./components/FilterSection/FilterSection";
import SearchExport from "./components/SearchExport/SearchExport";
import ResultTable from "./components/ResultTable/ResultTable";
import PaginationControls from "./components/PaginationControls/PaginationControls";
import ShippingHistory from "./components/ShippingHistory/ShippingHistory";

import {
    formatDateTime,
    formatHistoricoDocumentacaoDataHora,
} from "../../../../utils/date";
import {
    criarAvaliacaoSolicitacao,
    getAvaliacaoSolicitacoesLegacy,
    getAvaliacaoSolicitacoesByAno,
    getHistoricoAcaoQuestionario,
    getEventosSolicitacaoAvaliacao,
} from "../../../../services";
import { PATHS } from "../../../../routes/paths";
import {
    normalizeSituacaoNomeSindicato,
    normalizeV2ListItemDates,
    resolveV2SituacaoParaLista,
    rowSituacaoMatchesSelectedFilters,
} from "../../Analysis/shared/analysisListUtils";
import { pickComprovanteDespesasAssinadoListItem } from "../../../../domain/shipping/novaSolicitacaoSindicatoRule";

type ShippingRow = {
    id: number;
    solicitacao: string;
    situacao: string;
    envioDocumentacao: string | null;
    ultimaAtualizacao: string | null;
    solicitacaoAdequacao: string | null;
    adequacaoDocumentacao: string | null;
    conclusaoAnalise: string | null;
    ano: number;
    idQuestionarioResposta?: number | null;
    cnpj?: string | null;
    sigla?: string | null;
    historicoHabilitado?: boolean;
    comprovanteDespesasAssinado?: boolean;
};

type HistoryEntry = {
    acao: string;
    data: string;
    id?: number;
};

type SortConfig = {
    key: string | null;
    direction: string | null;
};

type HistoricoAcaoItem = {
    idAcaoQuestionario: number;
    nomeAcaoQuestionario: string;
    dataHora: string;
    idQuestionarioResposta: number;
};

type EventoSolicitacaoItem = {
    id?: number;
    nome?: string;
    criadoEm?: string;
    possuiComprovante?: boolean;
};

const STORAGE_KEY = "shipping_selector_filters_v1";

const ALL_SITUATIONS = [
    "Documentação Pendente",
    "Documentação Enviada",
    "Adequação Pendente",
    "Análise Concluída",
    "Inapto",
];

const resolveShippingVersion = (year: number) => {
    return year === 2025 ? "v1" : "v2";
};

const getAnoRequestParam = (year: number | null | undefined): number => {
    if (year === null || year === undefined) {
        return 2026;
    }
    return year;
};

const extractAnosModelo = (
    response: { avaliacoesModelo?: { anoReferencia: number }[] } | null | undefined,
): number[] => {
    const anos =
        response?.avaliacoesModelo?.map((item) => item.anoReferencia) ?? [];
    return [...new Set<number>(anos)].sort((a, b) => b - a);
};

const inferSituacaoFromDates = (item: any): string => {
    if (item?.analiseConcluidaEm) return "Análise Concluída";
    if (item?.adequacaoSolicitadaEm && !item?.adequacaoRealizadaEm) {
        return "Adequação Pendente";
    }
    if (item?.enviadoPeloSindicatoEm) return "Documentação Enviada";
    return "Documentação Pendente";
};

const mapLegacyToShippingRows = (items: any[]): ShippingRow[] => {
    if (!Array.isArray(items)) return [];

    return items.map((item, index) => {
        const situacaoApi = item?.situacao?.nome || null;
        const situacaoFinal = situacaoApi
            ? normalizeSituacaoNomeSindicato(situacaoApi)
            : inferSituacaoFromDates(item);

        const idQuestionarioResposta = item?.idQuestionarioResposta ?? null;

        return {
            id: idQuestionarioResposta ?? index + 1,
            solicitacao: item?.solicitacao || `${index + 1}ª Solicitação`,
            situacao: situacaoFinal,
            envioDocumentacao: item?.enviadoPeloSindicatoEm || null,
            ultimaAtualizacao: item?.atualizadoPeloSindicatoEm || null,
            solicitacaoAdequacao: item?.adequacaoSolicitadaEm || null,
            adequacaoDocumentacao: item?.adequacaoRealizadaEm || null,
            conclusaoAnalise: item?.analiseConcluidaEm || null,
            ano: 2025,
            idQuestionarioResposta,
            cnpj: item?.cnpj || null,
            sigla: item?.sigla || null,
            historicoHabilitado: !!idQuestionarioResposta && idQuestionarioResposta !== 0,
            comprovanteDespesasAssinado:
                pickComprovanteDespesasAssinadoListItem(item),
        };
    });
};

const mapV2ItemToShippingRow = (
    item: any,
    anoReferencia: number,
    index = 0,
): ShippingRow => {
    const datas = normalizeV2ListItemDates(item);
    const situacaoFinal = resolveV2SituacaoParaLista(
        item,
        normalizeSituacaoNomeSindicato,
    );

    const solicitacaoId = item?.solicitacaoId ?? null;
    const historicoHabilitado = !!solicitacaoId && solicitacaoId !== 0;

    return {
        id: solicitacaoId ?? index + 1,
        solicitacao: item?.solicitacao || `${index + 1}ª Solicitação`,
        situacao: situacaoFinal,
        envioDocumentacao: datas.enviadoPeloSindicatoEm,
        ultimaAtualizacao: datas.atualizadoPeloSindicatoEm,
        solicitacaoAdequacao: datas.adequacaoSolicitadaEm,
        adequacaoDocumentacao: datas.adequacaoRealizadaEm,
        conclusaoAnalise: datas.analiseConcluidaEm,
        ano: anoReferencia,
        idQuestionarioResposta: historicoHabilitado ? solicitacaoId : null,
        cnpj: item?.cnpj || null,
        sigla: item?.sigla || null,
        historicoHabilitado,
        comprovanteDespesasAssinado:
            pickComprovanteDespesasAssinadoListItem(item),
    };
};

const mapV2ToShippingRows = (
    items: any[],
    anoReferencia: number,
): ShippingRow[] => {
    if (!Array.isArray(items)) return [];
    return items.map((item, index) =>
        mapV2ItemToShippingRow(item, anoReferencia, index),
    );
};

const VersionSelector = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [data, setData] = useState<ShippingRow[]>([]);
    const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [creatingSolicitacao, setCreatingSolicitacao] = useState(false);
    const [novaSolicitacaoPermitida, setNovaSolicitacaoPermitida] = useState<boolean | null>(null);
    const [selectedSituation, setSelectedSituation] = useState<string[]>(
        ALL_SITUATIONS,
    );
    const [selectedYear, setSelectedYear] = useState<number>(2026);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: null,
        direction: null,
    });
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState<HistoryEntry[]>([]);

    const skipNextYearFetchRef = useRef(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;

            const parsed = JSON.parse(raw);

            if (Array.isArray(parsed?.selectedSituation)) {
                const sanitized = parsed.selectedSituation
                    .filter((s: unknown) => typeof s === "string")
                    .filter((s: string) => ALL_SITUATIONS.includes(s));

                setSelectedSituation(
                    sanitized.length > 0 ? sanitized : ALL_SITUATIONS,
                );
            }

            if (Number.isFinite(parsed?.currentPage)) {
                setCurrentPage(parsed.currentPage);
            }

            if (Number.isFinite(parsed?.itemsPerPage)) {
                setItemsPerPage(parsed.itemsPerPage);
            }

            if (parsed?.sortConfig && typeof parsed.sortConfig === "object") {
                setSortConfig({
                    key: parsed.sortConfig.key ?? null,
                    direction: parsed.sortConfig.direction ?? null,
                });
            }
        } catch (error) {
            console.warn("⚠️ Não foi possível restaurar filtros do envio:", error);
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    selectedSituation,
                    currentPage,
                    itemsPerPage,
                    sortConfig,
                }),
            );
        } catch (error) {
            console.warn("⚠️ Não foi possível salvar filtros do envio:", error);
        }
    }, [selectedSituation, currentPage, itemsPerPage, sortConfig]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            setInitialLoading(true);

            try {
                const response = await getAvaliacaoSolicitacoesByAno(null);

                const anoSelecionado = response?.anoReferenciaSelecionado ?? 2026;
                const anosDisponiveis =
                    response?.avaliacoesModelo?.map((item: any) => item.anoReferencia) ?? [];
                const solicitacoes = response?.solicitacoes ?? [];

                if (typeof response?.novaSolicitacao === "boolean") {
                    setNovaSolicitacaoPermitida(response.novaSolicitacao);
                }

                setAvailableYears(
                    anosDisponiveis.length > 0
                        ? [...new Set<number>(anosDisponiveis)].sort((a, b) => b - a)
                        : [2026, 2025],
                );

                skipNextYearFetchRef.current = true;
                setSelectedYear(anoSelecionado);

                if (anoSelecionado === 2025) {
                    const legacyResponse = await getAvaliacaoSolicitacoesLegacy();
                    const anosDisponiveis = extractAnosModelo(legacyResponse);
                    if (anosDisponiveis.length > 0) {
                        setAvailableYears(anosDisponiveis);
                    }
                    setData(
                        mapLegacyToShippingRows(legacyResponse?.solicitacoes ?? []),
                    );
                } else {
                    setData(mapV2ToShippingRows(solicitacoes, anoSelecionado));
                }
            } catch (error) {
                console.error("❌ Erro ao buscar solicitações iniciais:", error);
                setData([]);
            } finally {
                setLoading(false);
                setInitialLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        const st = location.state as { refreshSolicitacoes?: boolean } | null;
        if (!st?.refreshSolicitacoes) return;

        navigate(
            { pathname: location.pathname, search: location.search },
            { replace: true, state: {} },
        );

        const refetch = async () => {
            setLoading(true);
            try {
                const y = selectedYear;
                if (y === 2025) {
                    const legacyResponse = await getAvaliacaoSolicitacoesLegacy();
                    setData(
                        mapLegacyToShippingRows(legacyResponse?.solicitacoes ?? []),
                    );
                    return;
                }

                const response = await getAvaliacaoSolicitacoesByAno(
                    getAnoRequestParam(y),
                );
                const solicitacoes = response?.solicitacoes ?? [];
                if (typeof response?.novaSolicitacao === "boolean") {
                    setNovaSolicitacaoPermitida(response.novaSolicitacao);
                }
                setData(mapV2ToShippingRows(solicitacoes, y));
            } catch (e) {
                console.error("Erro ao atualizar lista após envio:", e);
            } finally {
                setLoading(false);
            }
        };

        void refetch();
    }, [location.state, location.pathname, location.search, navigate, selectedYear]);

    useEffect(() => {
        if (initialLoading || !selectedYear) return;

        if (skipNextYearFetchRef.current) {
            skipNextYearFetchRef.current = false;
            return;
        }

        const fetchDataByYear = async () => {
            setLoading(true);

            try {
                if (selectedYear === 2025) {
                    setNovaSolicitacaoPermitida(null);
                    const legacyResponse = await getAvaliacaoSolicitacoesLegacy();
                    const anosDisponiveis = extractAnosModelo(legacyResponse);
                    if (anosDisponiveis.length > 0) {
                        setAvailableYears(anosDisponiveis);
                    }
                    setData(
                        mapLegacyToShippingRows(legacyResponse?.solicitacoes ?? []),
                    );
                    return;
                }

                const response = await getAvaliacaoSolicitacoesByAno(
                    getAnoRequestParam(selectedYear),
                );
                const anosDisponiveis =
                    response?.avaliacoesModelo?.map((item: any) => item.anoReferencia) ?? [];
                const solicitacoes = response?.solicitacoes ?? [];

                if (anosDisponiveis.length > 0) {
                    setAvailableYears(
                        [...new Set<number>(anosDisponiveis)].sort((a, b) => b - a),
                    );
                }

                if (typeof response?.novaSolicitacao === "boolean") {
                    setNovaSolicitacaoPermitida(response.novaSolicitacao);
                }

                setData(mapV2ToShippingRows(solicitacoes, selectedYear));
            } catch (error) {
                console.error("❌ Erro ao buscar solicitações por ano:", error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDataByYear();
    }, [selectedYear, initialLoading]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedYear, selectedSituation, itemsPerPage]);

    const isYearLocked = selectedYear === 2025;

    const canStartMeritocracia = useMemo(() => {
        if (isYearLocked) {
            return false;
        }

        if (novaSolicitacaoPermitida !== null) {
            return novaSolicitacaoPermitida;
        }

        const validRows = data.filter(
            (item) =>
                Number(item.ano) === Number(selectedYear) &&
                item.id !== 0,
        );

        if (validRows.length === 0) {
            return true;
        }

        if (!validRows.every((item) => item.situacao === "Análise Concluída")) {
            return false;
        }

        return validRows.every(
            (item) => item.comprovanteDespesasAssinado === true,
        );
    }, [data, selectedYear, isYearLocked, novaSolicitacaoPermitida]);

    const hasSolicitacaoNoAno = useMemo(() => {
        return data.some(
            (item) =>
                Number(item.ano) === Number(selectedYear) &&
                item.id !== 0,
        );
    }, [data, selectedYear]);

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const matchesYear = Number(item.ano) === Number(selectedYear);

            const matchesSituation = rowSituacaoMatchesSelectedFilters(
                item.situacao,
                selectedSituation,
                ALL_SITUATIONS,
            );

            return matchesYear && matchesSituation;
        });
    }, [data, selectedYear, selectedSituation]);

    const sortedData = useMemo(() => {
        const list = [...filteredData];

        if (!sortConfig.key || !sortConfig.direction) return list;

        return list.sort((a, b) => {
            const getValue = (item: ShippingRow) => {
                switch (sortConfig.key) {
                    case "solicitacao":
                        return item.solicitacao;
                    case "situacao":
                        return item.situacao;
                    case "ultimaAtualizacao":
                        return new Date(item.ultimaAtualizacao || 0).getTime() || 0;
                    default:
                        return "";
                }
            };

            const aValue = getValue(a);
            const bValue = getValue(b);

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc"
                    ? aValue - bValue
                    : bValue - aValue;
            }

            return sortConfig.direction === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }, [filteredData, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;

    const currentItems = sortedData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
    );

    const handleSort = (key: string) => {
        let direction: string | null = "asc";
        let newKey: string | null = key;

        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        } else if (sortConfig.key === key && sortConfig.direction === "desc") {
            direction = null;
            newKey = null;
        }

        setSortConfig({ key: newKey, direction });
    };

    const getSortIcon = (key: string) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === "asc" ? "▲" : "▼";
        }
        return "↕";
    };

    const handleSituationFilter = (value: string | string[]) => {
        if (Array.isArray(value)) {
            setSelectedSituation(value);
            setCurrentPage(1);
            return;
        }

        if (value === "Todos") {
            const allSelected = selectedSituation.length === ALL_SITUATIONS.length;
            setSelectedSituation(allSelected ? [] : ALL_SITUATIONS);
            setCurrentPage(1);
            return;
        }

        setSelectedSituation((prev) =>
            prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
        );
        setCurrentPage(1);
    };

    const handleStartMeritocracia = async () => {
        if (!canStartMeritocracia || creatingSolicitacao || isYearLocked) {
            return;
        }

        try {
            setCreatingSolicitacao(true);

            const created = await criarAvaliacaoSolicitacao();

            if (created) {
                const novaLinha = mapV2ItemToShippingRow(created, selectedYear, 0);

                setData((prev) => {
                    const jaExiste = prev.some(
                        (item) =>
                            item.idQuestionarioResposta === novaLinha.idQuestionarioResposta ||
                            (item.solicitacao === novaLinha.solicitacao &&
                                item.ano === novaLinha.ano),
                    );

                    if (jaExiste) {
                        return prev;
                    }

                    return [novaLinha, ...prev];
                });

                navigate(PATHS.DOC_SHIPPING_V2, {
                    state: {
                        selectedYear,
                        item: novaLinha,
                        mode: "create",
                        idQuestionarioResposta: novaLinha.idQuestionarioResposta ?? null,
                    },
                });

                return;
            }

            navigate(PATHS.DOC_SHIPPING_V2, {
                state: { selectedYear },
            });
        } catch (error: any) {
            console.error("❌ Erro ao iniciar meritocracia:", error);

            if (error?.response?.status === 400 || error?.response?.status === 409) {
                const response = await getAvaliacaoSolicitacoesByAno(
                    getAnoRequestParam(selectedYear),
                );
                const solicitacoes = response?.solicitacoes ?? [];
                setData(mapV2ToShippingRows(solicitacoes, selectedYear));
            }
        } finally {
            setCreatingSolicitacao(false);
        }
    };

    const handleView = (item: ShippingRow) => {
        const version = resolveShippingVersion(item.ano);

        if (version === "v1") {
            navigate(PATHS.DOC_SHIPPING_V1, {
                state: {
                    selectedYear: item.ano,
                    item,
                    mode: "view",
                    idQuestionarioResposta: item.idQuestionarioResposta ?? null,
                },
            });
            return;
        }

        navigate(PATHS.DOC_SHIPPING_V2, {
            state: {
                selectedYear: item.ano,
                item,
                mode: "view",
                idQuestionarioResposta: item.idQuestionarioResposta ?? null,
            },
        });
    };

    const handleOpenHistory = async (item: ShippingRow) => {
        if (!item?.historicoHabilitado || !item?.idQuestionarioResposta) {
            return;
        }

        setSelectedHistory([]);
        setShowHistoryModal(true);
        setLoadingHistory(true);

        try {
            if (item.ano === 2025) {
                const response = await getHistoricoAcaoQuestionario(
                    item.idQuestionarioResposta,
                );

                const historico: HistoricoAcaoItem[] =
                    response?.data?.historicoAcaoQuestionario || [];

                const historyFormatted: HistoryEntry[] = historico.map(
                    (entry) => ({
                        acao: entry?.nomeAcaoQuestionario || "-",
                        data: formatHistoricoDocumentacaoDataHora(
                            entry?.dataHora,
                        ),
                    }),
                );

                setSelectedHistory(historyFormatted);
                return;
            }

            const response = await getEventosSolicitacaoAvaliacao(
                item.idQuestionarioResposta,
            );

            const eventosRaw = response?.data || response || [];
            const eventos: EventoSolicitacaoItem[] = Array.isArray(eventosRaw)
                ? eventosRaw
                : [eventosRaw];

            const historyFormatted: HistoryEntry[] = eventos.map((entry) => ({
                id: entry.id,
                acao: entry?.nome || "-",
                data: formatHistoricoDocumentacaoDataHora(entry?.criadoEm),
            }));

            setSelectedHistory(historyFormatted);
        } catch (error) {
            console.error("❌ Erro ao carregar histórico:", error);
            setSelectedHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleCloseHistory = () => {
        setShowHistoryModal(false);
        setSelectedHistory([]);
    };

    const startResult =
        sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);

    if (initialLoading || loading) return <PageLoading />;

    return (
        <Container fluid className="analysis-container shipping-version-container">
            <h1 className="analysis-title">SOLICITAÇÕES</h1>

            <>
                <div className="filters-actions-container shipping-filters-actions-container">
                    <FilterSection
                        selectedSituation={selectedSituation}
                        handleSituationFilter={handleSituationFilter}
                        allSituations={ALL_SITUATIONS}
                    />

                    <SearchExport
                        selectedYear={selectedYear}
                        setSelectedYear={setSelectedYear}
                        onStart={isYearLocked ? undefined : handleStartMeritocracia}
                        availableYears={availableYears}
                        loading={creatingSolicitacao}
                        disabledStart={!canStartMeritocracia}
                        showStartButton={!isYearLocked}
                        startButtonLabel={
                            selectedYear !== 2025 && hasSolicitacaoNoAno
                                ? "NOVA SOLICITAÇÃO"
                                : "INICIAR MERITOCRACIA"
                        }
                    />
                </div>

                <ResultTable
                    currentItems={currentItems}
                    handleSort={handleSort}
                    getSortIcon={getSortIcon}
                    onView={handleView}
                    onOpenHistory={handleOpenHistory}
                    historyLoading={loadingHistory}
                    formatDate={formatDateTime}
                />

                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    startResult={startResult}
                    endResult={endResult}
                    totalItems={sortedData.length}
                />

                <ShippingHistory
                    show={showHistoryModal}
                    onHide={handleCloseHistory}
                    historyData={selectedHistory}
                    loading={loadingHistory}
                />
            </>
        </Container>
    );
};

export default VersionSelector;